import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { ButtonComponent, InputComponent, TableComponent } from '@core/user-interface-legacy';

import { StatusBadgeComponent } from '../../components/status-badge/status-badge.component';
import { DynamicsDataService } from '../../services/dynamics-data.service';
import type { BranchSummary } from '../../types/dynamics.types';

@Component({
  selector: 'app-branches-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    DatePipe,
    StatusBadgeComponent,
    ButtonComponent,
    InputComponent,
    TableComponent,
  ],
  template: `
    <div class="toolbar">
      <rt-input
        class="search-input"
        placeholder="Search branches..."
        [value]="searchQuery()"
        (valueChange)="searchQuery.set($event)"
        [hasClearButton]="!!searchQuery()"
        (reset)="searchQuery.set('')"
      />
    </div>

    @if (dataService.loading() && !dataService.globalIndex()) {
      <div class="loading">Loading...</div>
    }

    @if (dataService.error(); as err) {
      <div class="error">{{ err }}</div>
    }

    @if (filteredBranches(); as branches) {
      <rt-table variant="outline">
        <thead>
          <tr>
            <th>Status</th>
            <th>Dynamic ID</th>
            <th>Branch</th>
            <th>Last Updated</th>
            <th>Actor</th>
            <th>Apps</th>
            <th>Pipeline</th>
          </tr>
        </thead>
        <tbody>
          @for (branch of branches; track branch.dynamicId) {
            <tr>
              <td>
                <app-status-badge [status]="branch.status" />
              </td>
              <td>
                <a [routerLink]="['/', branch.dynamicId]" class="link">{{ branch.dynamicId }}</a>
              </td>
              <td class="branch-name">{{ branch.branchName }}</td>
              <td>{{ branch.lastUpdatedAt | date: 'short' }}</td>
              <td>{{ branch.actor }}</td>
              <td>{{ branch.appCount }}</td>
              <td>
                @if (branch.pipelineUrl) {
                  <a [href]="branch.pipelineUrl" target="_blank" rel="noopener" class="link">
                    View
                  </a>
                }
              </td>
            </tr>
          } @empty {
            <tr>
              <td colspan="7" class="empty">No branches found</td>
            </tr>
          }
        </tbody>
      </rt-table>
    }
  `,
  styleUrl: './branches-list.component.less',
})
export class BranchesListComponent implements OnInit, OnDestroy {
  protected readonly dataService = inject(DynamicsDataService);
  protected readonly searchQuery = signal('');

  protected readonly filteredBranches = computed<BranchSummary[]>(() => {
    const index = this.dataService.globalIndex();
    if (!index) return [];

    const query = this.searchQuery().toLowerCase();
    if (!query) return index.branches;

    return index.branches.filter(
      b =>
        b.branchName.toLowerCase().includes(query) ||
        b.dynamicId.toLowerCase().includes(query) ||
        b.actor.toLowerCase().includes(query),
    );
  });

  public ngOnInit(): void {
    this.dataService.startPolling();
  }

  public ngOnDestroy(): void {
    this.dataService.stopPolling();
  }
}
